import type { ApiError as ApiErrorBody, MediaItem } from '@shared/types';

export type ErrorCode = ApiErrorBody['error']['code'] | 'network';

export class ApiError extends Error {
  code: ErrorCode;
  status: number;
  /** 'policy' = 403 because the current Privacy Policy / Terms version has not been accepted (the PolicyGate handles it). */
  reason?: 'policy';
  constructor(code: ErrorCode, message: string, status: number, reason?: 'policy') {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.reason = reason;
  }
}

export type MediaKind = 'avatar' | 'cover' | 'club_cover' | 'court_photo' | 'post' | 'partner_evidence' | 'event_poster' | 'slip' | 'video_poster' | 'game_photo' | 'comment_photo' | 'pay_qr' | 'booking_proof';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** don't open the login sheet on 401 (e.g. probing endpoints) */
  silent401?: boolean;
  signal?: AbortSignal;
}

// The auth provider registers a handler that opens the login sheet.
let unauthorizedHandler: ((returnTo: string) => void) | null = null;
export function setUnauthorizedHandler(fn: ((returnTo: string) => void) | null): void {
  unauthorizedHandler = fn;
}

// …and one that re-fetches /api/me when a 403 says the policy has not been accepted, so the PolicyGate appears.
let policyRequiredHandler: (() => void) | null = null;
export function setPolicyRequiredHandler(fn: (() => void) | null): void {
  policyRequiredHandler = fn;
}

export function currentPath(): string {
  return location.pathname + location.search;
}

function toError(status: number, data: unknown): ApiError {
  const body = data as Partial<ApiErrorBody> | null;
  if (body && typeof body === 'object' && body.error && typeof body.error.message === 'string') {
    return new ApiError(body.error.code ?? 'internal', body.error.message, status, body.error.reason === 'policy' ? 'policy' : undefined);
  }
  const fallback: Record<number, string> = {
    401: 'กรุณาเข้าสู่ระบบก่อน',
    403: 'คุณไม่มีสิทธิ์ทำรายการนี้',
    404: 'ไม่พบข้อมูลที่ต้องการ',
    409: 'ข้อมูลซ้ำหรือทำรายการนี้ไปแล้ว',
    429: 'ทำรายการถี่เกินไป ลองใหม่อีกครั้ง',
  };
  const code: ErrorCode =
    status === 401 ? 'unauthorized' : status === 403 ? 'forbidden' : status === 404 ? 'not_found' : status === 409 ? 'conflict' : status === 429 ? 'rate_limited' : status === 400 ? 'validation' : 'internal';
  return new ApiError(code, fallback[status] ?? `เกิดข้อผิดพลาด (${status})`, status);
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError('network', 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง', 0);
  }
  if (res.status === 204) return undefined as T;
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = toError(res.status, data);
    if (res.status === 401 && !opts.silent401 && unauthorizedHandler) unauthorizedHandler(currentPath());
    if (err.reason === 'policy' && policyRequiredHandler) policyRequiredHandler();
    throw err;
  }
  return data as T;
}

export const get = <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => api<T>(path, { ...opts, method: 'GET' });
export const post = <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => api<T>(path, { ...opts, method: 'POST', body });
export const patch = <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => api<T>(path, { ...opts, method: 'PATCH', body });
export const put = <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) => api<T>(path, { ...opts, method: 'PUT', body });
export const del = <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => api<T>(path, { ...opts, method: 'DELETE' });

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    sp.set(k, v === true ? '1' : String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** multipart upload with progress via XHR (fetch has no upload progress). */
export function uploadMedia(file: File, kind: MediaKind, onProgress?: (pct: number) => void, extra?: Record<string, string | number>): Promise<MediaItem> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/media');
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onerror = () => reject(new ApiError('network', 'อัปโหลดไม่สำเร็จ ลองใหม่อีกครั้ง', 0));
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve(data as MediaItem);
      } else {
        if (xhr.status === 401 && unauthorizedHandler) unauthorizedHandler(currentPath());
        const err = toError(xhr.status, data);
        if (err.reason === 'policy' && policyRequiredHandler) policyRequiredHandler();
        reject(err);
      }
    };
    const fd = new FormData();
    fd.append('kind', kind);
    for (const [k, v] of Object.entries(extra ?? {})) fd.append(k, String(v));
    fd.append('file', file, file.name);
    xhr.send(fd);
  });
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message || 'เกิดข้อผิดพลาด';
  return 'เกิดข้อผิดพลาด';
}
